import * as React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTheme, useMediaQuery } from "@mui/material";
import theme from "../../theme/theme";

const jobApplied = [0];
const hired = [0];
const resigned = [0];
const xLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function BarCharts({ seriesData, labels, maxValue }) {
  const materialUITheme = useTheme();
  const isDesktop = useMediaQuery(materialUITheme.breakpoints.up("md"));
  const isLargeMobile = useMediaQuery("(max-width: 768px)");
  const isSmallMobile = useMediaQuery("(max-width: 400px)");

  // If custom data is provided, render a single series for payslips
  const hasCustom = Array.isArray(seriesData) && seriesData.length > 0;
  const computedLabels = hasCustom ? (labels || xLabels) : xLabels;
  const computedSeries = hasCustom
    ? [{ data: seriesData, label: "Payslips", id: "payslips", color: theme.palette.primary.main }]
    : [
        { data: jobApplied, label: "Job Applied", id: "pvId", stack: "total", color: theme.palette.primary.main },
        { data: hired, label: "Hired", id: "hiId", stack: "total", color: theme.palette.lightGray.main },
        { data: resigned, label: "Resigned", id: "uvId", stack: "total", color: theme.palette.secondary.main },
      ];

  const providedMax = typeof maxValue === "number" && maxValue > 0 ? maxValue : null;
  const computedMax = providedMax ?? Math.max(...(hasCustom ? seriesData : jobApplied), 10);
  
  // Calculate step size: divide max by 12
  const stepSize = computedMax / 12;
  
  // Generate 13 tick values: 0 to max in 12 equal steps, ensuring last value is exactly max
  const tickValues = Array.from({ length: 13 }, (_, i) => {
    if (i === 12) {
      return computedMax; // Last tick is exactly the max value (345)
    }
    return Number((stepSize * i).toFixed(2));
  });
  
  // Ensure all values are proper numbers
  const cleanTickValues = tickValues.map(v => Number(v));
  

  return (
    <BarChart
      width={isDesktop ? 500 : (isLargeMobile ? 350 : (isSmallMobile ? 250 : "100%"))}
      height={isDesktop ? 300 : (isLargeMobile ? 250 : (isSmallMobile ? 200 : 150))}
      series={computedSeries}
      sx={{
        "& .MuiChartsAxis-tick text": {
          fontSize: "12px",
          margin: "0 5px",
          letterSpacing: "1px",
        },
        "& .MuiChartsAxis-line": {
          display: "none",
        },
        // Force Y-axis to show all ticks including max
        "& .MuiChartsAxis-root[data-axis-id='y']": {
          "& .MuiChartsAxis-tick": {
            opacity: 1,
            "&:last-child": {
              opacity: 1,
              display: "block !important",
            },
          },
          "& .MuiChartsAxis-tickLabel": {
            "&:last-child": {
              display: "block !important",
              opacity: 1,
            },
          },
        },
      }}
      xAxis={[
        {
          data: computedLabels,
          scaleType: "band",
          categoryGapRatio: 0.8, // more gap between month labels
          barGapRatio: 0.7,
          tickLabelStyle: { padding: "0 2px" },
          tickInterval: 1, // show every month tick
        },
      ]}
      yAxis={[
        {
          min: 0,
          max: computedMax + (computedMax * 0.001), // Slightly above max to ensure it shows
          nice: false,
          scaleType: "linear",
          tickNumber: 13,
          tickValues: cleanTickValues,
          tickLabelInterval: () => true, // Force show all ticks
          valueFormatter: (v) => {
            const num = Number(v);
            // Check if this is the max value (with small tolerance)
            if (Math.abs(num - computedMax) < 1) {
              return computedMax.toString();
            }
            // Check if it matches any tick value
            const matchingTick = cleanTickValues.find(tick => Math.abs(tick - num) < 1);
            if (matchingTick !== undefined) {
              if (Math.abs(matchingTick - computedMax) < 1) {
                return computedMax.toString();
              }
              return matchingTick.toFixed(2);
            }
            return num.toFixed(2);
          },
        },
      ]}
    />
  );
}